// Function index:
//   listAdminUsers — paginated/filterable user list.
//   getAdminUserDetail — per-user detail aggregation (parties/characters/spend/audit).
//   bulkUserAction — apply verify/disable/enable/reset_password to many users.
//   setUserRole / verifyUser / updateUserProfile / setUserDisabled / resetUserPassword / deleteUser — per-user account actions.
//   listAdminParties — party overview.
//   getAdminSystem — counters + recent job failures.
//   enqueueAdminJob — kick a specific gm_job kind for a party.
//   retryAdminJob / retryAllFailedAdminJobs — re-run a failed job, or all failed jobs.
//   getPartyPrefs / setPartyPrefs — read/patch a party's GM prefs.
//   endPartyCombat — force-end a party's wedged combat (escape hatch).
//   listAdminSecrets / setAdminSecret / deleteAdminSecret / listAdminSecretAudit — encrypted at-rest credentials + audit.
//   getAdminUsage — per-user spend breakdown by model and period.
//   listAdminAudit — paginated admin action audit log.
//   listAdminWorldRules — all world house rules (active + revoked).
//   revokeAdminWorldRule — revoke a single house rule by id.
//   listAdminCodexRules — all conditional codex rules (active + revoked).
//   revokeAdminCodexRule — revoke a single codex rule by id.
//   getAdminLlmModel / setAdminLlmModel / clearAdminLlmModel — the admin's global model override.
//   getAdminLlmCatalog — OpenRouter model catalog for the picker.
//   mintAdminWorld / listAdminWorldMints — mint themed worlds + list recent mints.
//   ADMIN_JOB_KINDS — enumerable gm_job kinds for the enqueue control.
//
// All hit /api/admin/* and require the session user to be role='admin'.

import { asError, getJson, postJson } from './http.js';
import type {
  AdminUser,
  AdminParty,
  AdminSystem,
  SecretMetadata,
  PartyPrefs,
  SecretAuditEntry,
  Period,
  AdminUsageUserData,
  AdminUsage,
  AdminAuditEntry,
  AdminAuditPage,
  AdminUsersPage,
  AdminUserDetail,
  BulkUserResult,
  AdminWorldRule,
  AdminCodexRule,
  AdminLlmCatalogModel,
  AdminWorldMint,
} from './types/admin.js';

export type {
  AdminUser,
  AdminParty,
  AdminSystem,
  SecretMetadata,
  PartyPrefs,
  SecretAuditEntry,
  Period,
  AdminUsageUserData,
  AdminUsage,
  AdminAuditEntry,
  AdminAuditPage,
  AdminUsersPage,
  AdminUserDetail,
  BulkUserResult,
  AdminWorldRule,
  AdminCodexRule,
  AdminLlmCatalogModel,
  AdminWorldMint,
};

export const ADMIN_JOB_KINDS = [
  'noop',
  'continuity', 'auto_lore', 'pacing', 'consequence', 'session_recap',
  'npc_sim_tick',
  'arc_session_summary', 'arc_summary', 'embeddings_index',
  'narrative_extract', 'director',
] as const;
export type AdminJobKind = typeof ADMIN_JOB_KINDS[number];

export const listAdminUsers = (params: {
  q?: string; role?: 'user' | 'admin'; status?: 'active' | 'disabled' | 'unverified'; page?: number; limit?: number;
} = {}): Promise<AdminUsersPage> => {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.role) sp.set('role', params.role);
  if (params.status) sp.set('status', params.status);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return getJson<AdminUsersPage>(`/api/admin/users${qs ? '?' + qs : ''}`);
};

export const getAdminUserDetail = (id: number): Promise<AdminUserDetail> =>
  getJson(`/api/admin/users/${id}`);

export const bulkUserAction = (
  ids: number[], action: 'verify' | 'disable' | 'enable' | 'reset_password',
): Promise<{ ok: true; results: BulkUserResult[] }> =>
  postJson('/api/admin/users/bulk', { ids, action });

export const setUserRole = (id: number, role: 'user' | 'admin') =>
  postJson<{ ok: true }>(`/api/admin/users/${id}/role`, { role });

export const verifyUser = (id: number) =>
  postJson<{ ok: true }>(`/api/admin/users/${id}/verify`, {});

export const updateUserProfile = (id: number, patch: { display_name?: string; email?: string }): Promise<{ ok: true }> =>
  postJson(`/api/admin/users/${id}/profile`, patch);

export const setUserDisabled = (id: number, disabled: boolean): Promise<{ ok: true }> =>
  postJson(`/api/admin/users/${id}/disabled`, { disabled });

export const resetUserPassword = (id: number): Promise<{ ok: true }> =>
  postJson(`/api/admin/users/${id}/reset-password`, {});

export async function deleteUser(id: number): Promise<{ ok: true }> {
  const res = await fetch(`/api/admin/users/${id}`, {
    method: 'DELETE', credentials: 'same-origin',
  });
  if (!res.ok) throw await asError(res);
  return res.json();
}

export const listAdminParties = (): Promise<{ parties: AdminParty[] }> => getJson('/api/admin/parties');

export const getAdminSystem = (): Promise<AdminSystem> => getJson('/api/admin/system');

export const enqueueAdminJob = (party_id: number, kind: AdminJobKind) =>
  postJson<{ ok: true; job_id: number }>('/api/admin/jobs', { party_id, kind });

export const retryAdminJob = (id: number) =>
  postJson<{ ok: true; updated: number }>(`/api/admin/jobs/${id}/retry`, {});

export const retryAllFailedAdminJobs = () =>
  postJson<{ ok: true; updated: number }>('/api/admin/jobs/retry-failed', {});

export const getPartyPrefs = (id: number): Promise<{ prefs: PartyPrefs }> =>
  getJson(`/api/admin/parties/${id}/prefs`);

export const setPartyPrefs = (id: number, patch: Partial<PartyPrefs>) =>
  postJson<{ ok: true; prefs: PartyPrefs }>(`/api/admin/parties/${id}/prefs`, patch);

export const endPartyCombat = (id: number) =>
  postJson<{ ok: true; combat_id: number }>(`/api/admin/parties/${id}/end-combat`, {});

export const listAdminSecrets = (): Promise<{ kek_configured: boolean; secrets: SecretMetadata[] }> =>
  getJson('/api/admin/secrets');

export const setAdminSecret = (name: string, value: string, description?: string) =>
  postJson<{ ok: true }>(`/api/admin/secrets/${encodeURIComponent(name)}`, { value, description });

export async function deleteAdminSecret(name: string): Promise<{ ok: true }> {
  const res = await fetch(`/api/admin/secrets/${encodeURIComponent(name)}`, {
    method: 'DELETE', credentials: 'same-origin',
  });
  if (!res.ok) throw await asError(res);
  return res.json();
}

export const listAdminSecretAudit = (name?: string): Promise<{ entries: SecretAuditEntry[] }> =>
  getJson(`/api/admin/secrets/audit${name ? '?name=' + encodeURIComponent(name) : ''}`);

export const getAdminUsage = (period: Period): Promise<AdminUsage> =>
  getJson<AdminUsage>(`/api/admin/usage?period=${encodeURIComponent(period)}`);

export const listAdminAudit = (params: { q?: string; action?: string; page?: number; limit?: number } = {}): Promise<AdminAuditPage> => {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.action) sp.set('action', params.action);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return getJson<AdminAuditPage>(`/api/admin/audit${qs ? '?' + qs : ''}`);
};

export const listAdminWorldRules = (): Promise<{ rules: AdminWorldRule[] }> =>
  getJson('/api/admin/world-rules');

export const revokeAdminWorldRule = (id: number): Promise<{ ok: boolean; revoked: boolean }> =>
  postJson(`/api/admin/world-rules/${id}/revoke`, {});

export const listAdminCodexRules = (): Promise<{ rules: AdminCodexRule[] }> =>
  getJson('/api/admin/codex-rules');

export const revokeAdminCodexRule = (id: number): Promise<{ ok: boolean; revoked: boolean }> =>
  postJson(`/api/admin/codex-rules/${id}/revoke`, {});

export const getAdminLlmModel = (): Promise<{ model: string | null; curated: Array<{ id: string; label: string }> }> =>
  getJson('/api/admin/llm-model');

export const getAdminLlmCatalog = (): Promise<{ models: AdminLlmCatalogModel[] }> =>
  getJson('/api/admin/llm-model/catalog');

export const setAdminLlmModel = (model: string): Promise<{ ok: true }> =>
  postJson('/api/admin/llm-model', { model });

export async function clearAdminLlmModel(): Promise<{ ok: true }> {
  const res = await fetch('/api/admin/llm-model', {
    method: 'DELETE', credentials: 'same-origin',
  });
  if (!res.ok) throw await asError(res);
  return res.json();
}

export const mintAdminWorld = (body: {
  theme: string; name: string; brief: string; size?: number; seed?: number; spawn_prose_radius?: number;
}): Promise<{ mint_id: number }> => postJson('/api/admin/worlds/mint', body);

export const listAdminWorldMints = (): Promise<{ mints: AdminWorldMint[] }> =>
  getJson('/api/admin/worlds/mints');
